// SR-PDF — Slip Repair share PDF generator.
//
// Per founder ruling ("use the same template we used for SLIP
// EXTRACTOR"), this module is a structural port of
// `src/extractor/shareCardPdf.ts`. Same library (jsPDF, lazily
// imported), same palette (INK / SUB / HAIR / AMBER / AMBER_SOFT /
// TINT), same A4 portrait page, same letterhead rule, same chevron
// mark. The DOMAIN is different — repair surfaces an original→
// repaired comparison rather than an extraction receipt — but the
// visual language is identical.
//
// Doctrine (STR-PROD-008 v2.1.1 §5): this PDF MUST NOT contain
// agent names, CLV/probability magnitudes, fair_prob values,
// substrate / IFPE / Voulgaris vocabulary, or per-league/per-market
// win rates. The companion module `pdfLint.ts` enforces this with a
// regex deny-list; the unit test in __tests__ asserts a real
// generated PDF emits zero matches.

import type { jsPDF } from "jspdf";

type RGB = [number, number, number];

const A4_W = 595.28;
const A4_H = 841.89;
const M = 48; // page margin (pt)

const INK: RGB = [22, 20, 15];
const SUB: RGB = [111, 108, 102];
const HAIR: RGB = [231, 228, 221];
const AMBER: RGB = [212, 168, 67];
const AMBER_SOFT: RGB = [231, 207, 152];
const TINT: RGB = [247, 246, 243];

// Decision badge colours — kept perceptually close to the in-app
// LegCard palette so the PDF reads as a printed continuation of
// the screen, not a re-skin.
const C_RETAIN: RGB = [60, 138, 80];     // green
const C_REPLACE: RGB = [184, 142, 47];   // gold
const C_FLAG: RGB = [196, 121, 30];      // amber
const C_MARGINAL: RGB = [80, 110, 170];  // blue
const C_UNRESOLVED: RGB = [120, 118, 113]; // grey
const C_VOID_HIGH: RGB = [200, 60, 60];
const C_VOID_MED: RGB = [200, 140, 40];

// ─── Public model ───────────────────────────────────────────────
// The renderer is pure given this model. Fetch + assembly is in
// `loadRepairModel()` below (browser-only — calls supabase).

export interface RepairPdfLeg {
  /** 1-based leg number from the slip. */
  n: number;
  /** "Home vs Away" — verbatim slip text. */
  match: string;
  /** Decision badge — uppercase. */
  decision: "RETAIN" | "REPLACE" | "FLAG" | "MARGINAL" | "UNRESOLVED";
  /** HIGH / MEDIUM / LOW / null. */
  voidRisk: "HIGH" | "MEDIUM" | "LOW" | null;
  /** Original selection text (verbatim from the slip). */
  originalMarket: string;
  originalSelection: string;
  originalOdds: number | null;
  /** Replacement — only present for REPLACE / MARGINAL legs that
   *  produced a substrate-backed alternative. */
  replacementMarket: string | null;
  replacementSelection: string | null;
  replacementOdds: number | null;
  /** Verbatim Expert-Voice rationale (already doctrine-clean — do
   *  not regenerate). 1–2 sentences, plain English. */
  reasoning: string;
}

export interface RepairPdfExcludedMarket {
  /** Plain-English market label (already humanized by the caller). */
  label: string;
  /** Short reason — verbatim from `repair_catalogue_markets.exclusion_reason`. */
  reason: string;
}

export interface RepairPdfModel {
  /** Short, public-safe slip code (first 12 chars of session_id, upper). */
  slipCode: string;
  bookmaker: string;
  /** ISO timestamp the repair completed. Rendered as a date string. */
  repairedAt: string | null;
  legCount: number;
  /** Slip-level scores. */
  healthBefore: number | null;
  healthAfter: number | null;
  /** Combined-odds totals. Null when not yet derived. */
  originalOdds: number | null;
  /** SR-TIERS — active-tier combined odds (post user actions). */
  sharpOdds: number | null;
  saferOdds: number | null;
  /** Human-readable tier label for the cover (Bold / Moderate / …). */
  tierLabel?: string;
  /** Void exposure counts (HIGH-risk legs before vs after). */
  voidHighBefore: number;
  voidHighAfter: number;
  /** Verbatim executive summary — already doctrine-clean. */
  slipSummary: string | null;
  legs: RepairPdfLeg[];
  /** Up to 5 markets from the published exclusion list (trust play). */
  excludedMarkets: RepairPdfExcludedMarket[];
  /** Full share URL (with referral code). */
  shareUrl: string;
  /** Referral code for the attribution line. */
  referralCode: string | null;
  /** Display handle for the attribution line ("Repaired by X"). */
  ownerHandle: string;
}

// ─── Formatters ─────────────────────────────────────────────────

function fmtOdds(n: number | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return "—";
  if (n >= 100) return `${Math.round(n).toLocaleString()}×`;
  return `${n.toFixed(2)}×`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function decisionColor(d: RepairPdfLeg["decision"]): RGB {
  switch (d) {
    case "RETAIN": return C_RETAIN;
    case "REPLACE": return C_REPLACE;
    case "FLAG": return C_FLAG;
    case "MARGINAL": return C_MARGINAL;
    case "UNRESOLVED": return C_UNRESOLVED;
    default: return SUB;
  }
}

function voidColor(v: RepairPdfLeg["voidRisk"]): RGB {
  if (v === "HIGH") return C_VOID_HIGH;
  if (v === "MEDIUM") return C_VOID_MED;
  return SUB;
}

/** Two-chevron brand mark — identical geometry to shareCardPdf. */
function drawMark(doc: jsPDF, ox: number, oy: number, s: number): void {
  const para = (pts: Array<[number, number]>, col: RGB): void => {
    const [a, b, c, d] = pts.map(
      ([px, py]) => [ox + px * s, oy + py * s] as [number, number],
    );
    if (!a || !b || !c || !d) return;
    doc.setFillColor(col[0], col[1], col[2]);
    doc.triangle(a[0], a[1], b[0], b[1], c[0], c[1], "F");
    doc.triangle(a[0], a[1], c[0], c[1], d[0], d[1], "F");
  };
  para([[4, 6], [20, 6], [28, 14], [12, 14]], AMBER);
  para([[4, 18], [20, 18], [28, 26], [12, 26]], AMBER_SOFT);
}

// ─── Pure render ────────────────────────────────────────────────

/** Render the repair report into a jsPDF doc (A4 portrait, pt).
 *  Pure given the model — exported for unit testing. */
export function renderRepairPdf(doc: jsPDF, model: RepairPdfModel): void {
  const text = (
    str: string,
    x: number,
    y: number,
    opts?: { align?: "left" | "right" | "center"; charSpace?: number },
  ): void => {
    doc.text(str, x, y, opts as never);
  };

  // ── Page 1 — Cover ──
  // Letterhead accent
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, A4_W, 5, "F");

  // Brand lockup, top-right
  drawMark(doc, A4_W - M - 132, 60, 0.85);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12.5);
  doc.setTextColor(...INK);
  text("STRIKZA", A4_W - M - 132 + 32, 79, { charSpace: 1.2 });

  // Eyebrow + title
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...SUB);
  text("SLIP REPAIR", M, 64, { charSpace: 2 });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...INK);
  text("Repair report", M, 90);

  // Metadata, right column
  let my = 126;
  const metaRow = (k: string, v: string): void => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...SUB);
    text(k, A4_W - M - 170, my);
    doc.setTextColor(...INK);
    text(v, A4_W - M, my, { align: "right" });
    my += 16;
  };
  metaRow("Bookmaker", model.bookmaker || "—");
  metaRow("Legs", String(model.legCount));
  metaRow("Repaired", fmtDate(model.repairedAt));
  metaRow("Slip ref", model.slipCode);

  // Hero: combined odds — Original → Active tier. Two-column, no
  // void section. ASCII ">" instead of unicode arrow so the default
  // helvetica embedding doesn't fall back to a tofu glyph.
  let oy = 174;
  doc.setFillColor(...TINT);
  doc.rect(M - 10, oy - 18, A4_W - 2 * M + 20, 84, "F");
  const tierLabel = (model.tierLabel ?? "REPAIRED").toUpperCase();
  const colW = (A4_W - 2 * M) / 2;
  const oddsCol = (label: string, value: string, x: number, accent: RGB): void => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...SUB);
    text(label, x, oy, { charSpace: 1.6 });
    doc.setFont("courier", "bold");
    doc.setFontSize(22);
    doc.setTextColor(accent[0], accent[1], accent[2]);
    text(value, x, oy + 30);
  };
  oddsCol("ORIGINAL", fmtOdds(model.originalOdds), M, INK);
  oddsCol(tierLabel, fmtOdds(model.sharpOdds), M + colW, AMBER);

  // Small caption under the pair.
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SUB);
  text("Combined odds reflect the legs you've accepted.", M, oy + 56);

  // Hairline under hero block.
  let vy = oy + 78;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.7);
  doc.line(M, vy, A4_W - M, vy);

  // Executive summary
  vy += 26;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...INK);
  text("Summary", M, vy);
  vy += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  const summary = (model.slipSummary ?? "").trim() ||
    "STRIKZA reviewed every leg on this slip and proposed a Sharp and a Safer variant where a measurable improvement was available. Original picks are kept where they already stood up.";
  const summaryLines = doc.splitTextToSize(summary, A4_W - 2 * M) as string[];
  summaryLines.forEach((ln, i) => {
    text(ln, M, vy + i * 14);
  });

  // Footer (page 1)
  drawCoverFooter(doc, model);

  // ── Pages 2+ — per-leg detail ──
  doc.addPage();
  let y = M + 6;
  drawPageHeader(doc, model);
  y = M + 56;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...SUB);
  text("LEG-BY-LEG DETAIL", M, y, { charSpace: 1.5 });
  y += 6;
  doc.setDrawColor(...HAIR);
  doc.line(M, y, A4_W - M, y);
  y += 14;

  for (const leg of model.legs) {
    // Approximate height; if it won't fit, paginate.
    const approxH = estimateLegHeight(doc, leg);
    if (y + approxH > A4_H - 90) {
      doc.addPage();
      drawPageHeader(doc, model);
      y = M + 56;
    }

    // Leg number + match
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...SUB);
    text(`LEG ${leg.n}`, M, y, { charSpace: 1.2 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...INK);
    // Truncate match if it would crash into the right-edge badge.
    const matchMaxW = A4_W - M - (M + 42) - 80;
    let matchTxt = leg.match || "—";
    while (doc.getTextWidth(matchTxt) > matchMaxW && matchTxt.length > 4) {
      matchTxt = matchTxt.slice(0, -2);
    }
    if (matchTxt !== (leg.match || "—")) matchTxt += "…";
    text(matchTxt, M + 42, y);

    // Decision badge (right-aligned chip)
    const dCol = decisionColor(leg.decision);
    doc.setFillColor(dCol[0], dCol[1], dCol[2]);
    const badgeText = leg.decision;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    const badgeW = doc.getTextWidth(badgeText) + 14;
    doc.rect(A4_W - M - badgeW, y - 10, badgeW, 14, "F");
    doc.setTextColor(255, 255, 255);
    text(badgeText, A4_W - M - badgeW / 2, y, { align: "center", charSpace: 0.8 });

    y += 22;

    // Active line — what the user is taking to the bookmaker after
    // their accept/override decisions. Big, bold, on its own row so
    // it never collides with the right-edge badge.
    const activeLabel = leg.decision === "REPLACE" || leg.decision === "MARGINAL"
      ? "STRIKZA PICK" : "YOUR LEG";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...SUB);
    text(activeLabel, M, y, { charSpace: 1.4 });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...INK);
    const activeSelTxt = leg.originalSelection && leg.originalSelection !== "—"
      ? ` · ${leg.originalSelection}` : "";
    const activeLine = `${leg.originalMarket || "—"}${activeSelTxt}`;
    text(activeLine, M + 96, y);
    doc.setFont("courier", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...AMBER);
    text(leg.originalOdds != null ? leg.originalOdds.toFixed(2) : "—", A4_W - M, y, { align: "right" });
    y += 16;

    // Alternative line ("was" or "instead of") — muted, smaller.
    if (leg.replacementSelection && leg.replacementMarket) {
      const altLabel = leg.decision === "REPLACE" || leg.decision === "MARGINAL" ? "WAS" : "ALSO CONSIDERED";
      doc.setFont("helvetica", "normal");
      doc.setFontSize(7.5);
      doc.setTextColor(...SUB);
      text(altLabel, M, y, { charSpace: 1.4 });
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...SUB);
      const altSelTxt = leg.replacementSelection ? ` · ${leg.replacementSelection}` : "";
      const altLine = `${leg.replacementMarket}${altSelTxt}`;
      text(altLine, M + 96, y);
      doc.setFont("courier", "normal");
      doc.setFontSize(9.5);
      text(leg.replacementOdds != null ? leg.replacementOdds.toFixed(2) : "—", A4_W - M, y, { align: "right" });
      y += 16;
    }

    // Reasoning (verbatim Expert Voice — doctrine-clean)
    if (leg.reasoning && leg.reasoning.trim()) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9.5);
      doc.setTextColor(...SUB);
      const lines = doc.splitTextToSize(leg.reasoning.trim(), A4_W - 2 * M) as string[];
      lines.forEach((ln, i) => {
        text(ln, M, y + i * 13);
      });
      y += lines.length * 13;
    }

    // Hairline divider between legs
    y += 12;
    doc.setDrawColor(...HAIR);
    doc.setLineWidth(0.5);
    doc.line(M, y, A4_W - M, y);
    y += 14;
  }

  // ── Last page — trust footer ──
  doc.addPage();
  drawPageHeader(doc, model);
  let ty = M + 56;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  text("Markets STRIKZA never suggests — and why", M, ty);
  ty += 8;
  doc.setDrawColor(...HAIR);
  doc.line(M, ty, A4_W - M, ty);
  ty += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...SUB);
  text(
    "We publish the markets we will not propose to you — and the reason. This is the trust contract.",
    M, ty,
  );
  ty += 18;

  for (const m of model.excludedMarkets) {
    if (ty > A4_H - 140) break;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...INK);
    text(`· ${m.label}`, M, ty);
    ty += 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...SUB);
    const lines = doc.splitTextToSize(m.reason, A4_W - 2 * M - 16) as string[];
    lines.forEach((ln, i) => text(ln, M + 14, ty + i * 12));
    ty += lines.length * 12 + 8;
  }

  // Bottom: referral attribution + share URL + legal
  const fy = A4_H - 90;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.7);
  doc.line(M, fy, A4_W - M, fy);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  const attribution = model.referralCode
    ? `Repaired by ${model.ownerHandle}'s STRIKZA · join via ${model.shareUrl}`
    : `Repaired by ${model.ownerHandle}'s STRIKZA · ${model.shareUrl}`;
  text(attribution, M, fy + 18);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...SUB);
  text(
    "Entertainment intelligence. Not investment advice. STRIKZA does not place bets. 18+.",
    M, fy + 36,
  );
}

function drawCoverFooter(doc: jsPDF, model: RepairPdfModel): void {
  const fy = A4_H - 54;
  doc.setDrawColor(...HAIR);
  doc.setLineWidth(0.7);
  doc.line(M, fy - 24, A4_W - M, fy - 24);
  drawMark(doc, M, fy - 18, 0.58);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(...SUB);
  doc.text("STRIKZA · Slip Repair", M + 24, fy - 6);
  doc.text(model.shareUrl, A4_W - M, fy - 6, { align: "right" } as never);
  doc.setFontSize(7);
  doc.text(
    "Entertainment intelligence · Not investment advice · 18+",
    M, fy + 9,
  );
}

function drawPageHeader(doc: jsPDF, model: RepairPdfModel): void {
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, A4_W, 3, "F");
  drawMark(doc, M, 24, 0.5);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text("STRIKZA", M + 20, 38, { charSpace: 1.1 } as never);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...SUB);
  doc.text(`Slip Repair · ${model.slipCode}`, A4_W - M, 38, { align: "right" } as never);
}

function estimateLegHeight(doc: jsPDF, leg: RepairPdfLeg): number {
  // Header + original line + (replacement?) + reasoning lines + divider.
  let h = 22 + 16; // header + orig
  if (leg.replacementSelection) h += 16;
  if (leg.reasoning && leg.reasoning.trim()) {
    const lines = doc.splitTextToSize(leg.reasoning.trim(), A4_W - 2 * M) as string[];
    h += lines.length * 13;
  }
  h += 26; // divider + gap
  return h;
}

// ─── Public entry: generate + download ──────────────────────────

/** Build the PDF and trigger a browser download. Lazily imports
 *  jsPDF so it never enters the main bundle. Caller is responsible
 *  for assembling the (doctrine-clean) model — see `loadRepairModel`. */
export async function downloadRepairPdf(model: RepairPdfModel): Promise<void> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  renderRepairPdf(doc, model);
  const filename = `strikza-repair-${(model.slipCode || "slip").toLowerCase()}.pdf`;
  doc.save(filename);
}

/** Build the PDF and return a Blob (for upload / preview). Same
 *  lazy-import path as `downloadRepairPdf`. */
export async function generateRepairPdfBlob(model: RepairPdfModel): Promise<Blob> {
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  renderRepairPdf(doc, model);
  return doc.output("blob");
}
